import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { Paperclip, Trash, DownloadSimple, FilePdf, FileImage, FileText, FileZip, FileVideo, FileAudio, File as FileIcon } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useConfirm } from './ConfirmDialog';
import { getAvatarStyle, getInitials } from '../lib/avatarColor';

// Map mime → phosphor icon. Keep the table small & readable.
const iconForMime = (mt = '') => {
  if (mt.startsWith('image/')) return FileImage;
  if (mt === 'application/pdf') return FilePdf;
  if (mt.startsWith('video/')) return FileVideo;
  if (mt.startsWith('audio/')) return FileAudio;
  if (mt.includes('zip') || mt.includes('compressed') || mt.includes('tar')) return FileZip;
  if (mt.startsWith('text/') || mt.includes('json') || mt.includes('xml') ||
      mt.includes('document') || mt.includes('spreadsheet') || mt.includes('presentation')) return FileText;
  return FileIcon;
};

const fmtBytes = (n) => {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const timeAgo = (ds) => {
  if (!ds) return '';
  const d = Date.now() - new Date(ds).getTime();
  if (d < 60000) return 'just now';
  if (d < 3600000) return `${Math.floor(d / 60000)}m ago`;
  if (d < 86400000) return `${Math.floor(d / 3600000)}h ago`;
  return new Date(ds).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// AttachmentsSection — Jira-style attachment list under a task.
// Drag-drop OR click to upload. Inline thumbnails for images. Download / delete.
export default function AttachmentsSection({ taskId, canEdit, currentUserId }) {
  const { getOrgHeaders, API, accessToken } = useAuth();
  const { caps, status: subStatus } = useSubscription();
  // Backend hard-defaults: Free=5MB, Business=10MB. Read from subscription
  // capabilities (overridable by SA flags) and fall back to a sane minimum
  // so we always block before hitting the network.
  const maxUploadMb = Number(caps?.max_upload_size_mb ?? (subStatus?.plan === 'free' ? 5 : 10));
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const blobCache = useRef({}); // id -> blob URL for previews

  const headers = useCallback(() => ({ withCredentials: true, headers: getOrgHeaders() }), [getOrgHeaders]);

  const fetchAttachments = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/tasks/${taskId}/attachments`, headers());
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [taskId, API, headers]);

  useEffect(() => { fetchAttachments(); }, [fetchAttachments]);

  // Cleanup any object URLs on unmount.
  useEffect(() => () => {
    Object.values(blobCache.current).forEach(u => { try { URL.revokeObjectURL(u); } catch (_) {} });
    blobCache.current = {};
  }, []);

  const uploadFiles = async (files) => {
    if (!files || !files.length) return;
    // Client-side size guard — show a clear, plan-aware error BEFORE we
    // send the bytes (saves a roundtrip + gives an actionable message).
    const limitBytes = Math.max(1, maxUploadMb) * 1024 * 1024;
    const oversized = Array.from(files).filter(f => f.size > limitBytes);
    const okFiles = Array.from(files).filter(f => f.size <= limitBytes);
    for (const f of oversized) {
      const fileMb = (f.size / 1024 / 1024).toFixed(1);
      toast.error(
        `"${f.name}" is ${fileMb} MB, which exceeds your plan's ${maxUploadMb} MB limit. Please reduce the file size and try uploading again.`,
        { duration: 6000 }
      );
    }
    if (!okFiles.length) return;
    setUploading(true);
    let successCount = 0;
    for (const f of okFiles) {
      try {
        const form = new FormData();
        form.append('file', f);
        await axios.post(`${API}/tasks/${taskId}/attachments`, form, {
          ...headers(),
          headers: { ...getOrgHeaders(), 'Content-Type': 'multipart/form-data' },
        });
        successCount++;
      } catch (err) {
        // Try to surface the most useful error possible. The backend returns
        // a structured `{ detail }` JSON (e.g., "10 MB plan limit exceeded"),
        // but if the request never reaches the backend — typically because
        // nginx in front of the API has a too-small `client_max_body_size`
        // (default 1 MB) — we get an opaque 413 with HTML body and no
        // `detail`. Fall back to status-code-aware messages so the user
        // (and ops team) can quickly identify the layer that failed.
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        const fileMb = (f.size / 1024 / 1024).toFixed(1);
        let msg;
        if (detail) {
          msg = detail;
        } else if (status === 413) {
          msg = `"${f.name}" (${fileMb} MB) was rejected by the server's request size limit. Please ask your administrator to raise nginx \`client_max_body_size\` to at least 50M.`;
        } else if (status === 504 || status === 502) {
          msg = `Upload of "${f.name}" timed out. Please try again or contact support.`;
        } else if (!err.response) {
          msg = `Network error uploading "${f.name}". Check your connection and retry.`;
        } else {
          msg = `Failed to upload ${f.name} (HTTP ${status || 'unknown'})`;
        }
        toast.error(msg, { duration: 7000 });
      }
    }
    if (successCount > 0) {
      toast.success(`${successCount} file${successCount > 1 ? 's' : ''} attached`);
      fetchAttachments();
    }
    setUploading(false);
  };

  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) uploadFiles(files);
    e.target.value = ''; // allow re-uploading same file
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (!canEdit) return;
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) uploadFiles(files);
  };

  const handleDelete = async (att) => {
    if (!(await confirm({
      title: 'Delete attachment?',
      description: `"${att.original_filename}" will be removed from this task.`,
      confirmText: 'Delete',
      variant: 'destructive',
    }))) return;
    try {
      await axios.delete(`${API}/attachments/${att.attachment_id}`, headers());
      setItems(prev => prev.filter(a => a.attachment_id !== att.attachment_id));
      toast.success('Attachment removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  // For image previews — fetch as blob (auth-token cannot be passed in <img src>).
  const ImageThumb = ({ att }) => {
    const [src, setSrc] = useState(blobCache.current[att.attachment_id] || null);
    useEffect(() => {
      let revoked = false;
      if (blobCache.current[att.attachment_id]) return;
      (async () => {
        try {
          const res = await axios.get(`${API}/attachments/${att.attachment_id}/download`, {
            ...headers(), responseType: 'blob',
          });
          if (revoked) return;
          const url = URL.createObjectURL(res.data);
          blobCache.current[att.attachment_id] = url;
          setSrc(url);
        } catch (_) {}
      })();
      return () => { revoked = true; };
    }, [att.attachment_id]);
    if (!src) {
      return <div className="w-full h-24 bg-zinc-100 rounded-md flex items-center justify-center"><FileImage size={20} className="text-zinc-400" /></div>;
    }
    return <img src={src} alt={att.original_filename} className="w-full h-24 object-cover rounded-md border border-zinc-200" />;
  };

  // Smart open: images & PDFs preview in a new browser tab; other files
  // (docs, zips, videos) trigger a download since the browser can't render them inline.
  // Attachments use plain <a target="_blank" href> with the backend URL.
  // Auth flows through the `access_token` + `current_org_id` cookies set on
  // login, so the browser opens the file natively without any JS popup-blocker
  // dance. Backend serves Content-Disposition: inline for images/PDFs (which
  // browsers preview) and attachment for everything else (which downloads).

  return (
    <div data-testid="attachments-section">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Paperclip size={14} className="text-zinc-500" />
          <span className="text-[12px] font-bold text-zinc-700 uppercase tracking-wide">
            Attachments {items.length > 0 && <span className="text-zinc-400">({items.length})</span>}
          </span>
        </div>
        {canEdit && (
          <Button
            size="sm" variant="outline" onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="h-7 text-[11px]"
            data-testid="attachment-upload-button"
          >
            <Paperclip size={12} className="mr-1" />
            {uploading ? 'Uploading…' : 'Attach'}
          </Button>
        )}
        <input ref={fileInputRef} type="file" multiple onChange={onPickFiles}
          className="hidden" data-testid="attachment-file-input" />
      </div>

      {/* Drop zone + list */}
      <div
        onDragOver={(e) => { e.preventDefault(); if (canEdit) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`rounded-lg border-2 border-dashed transition-colors ${
          dragOver ? 'border-blue-400 bg-blue-50/50' : 'border-zinc-200 bg-zinc-50/50'
        }`}
        data-testid="attachment-dropzone"
      >
        {items.length === 0 ? (
          <div className="text-center py-6 px-4">
            <Paperclip size={20} className="mx-auto text-zinc-300 mb-1.5" />
            <p className="text-[12px] text-zinc-500">
              {canEdit ? <>Drag &amp; drop files here, or <button onClick={() => fileInputRef.current?.click()} className="text-blue-600 hover:underline font-medium" data-testid="attachment-empty-cta">browse</button></> : 'No attachments'}
            </p>
            <p className="text-[10px] text-zinc-400 mt-0.5">Up to {maxUploadMb}MB per file. PDFs, images, designs, docs.</p>
          </div>
        ) : (
          <div className="p-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {items.map(att => {
              const Icon = iconForMime(att.mime_type);
              const isImage = (att.mime_type || '').startsWith('image/');
              const isPdf = att.mime_type === 'application/pdf';
              const inlineable = isImage || isPdf;
              const canDelete = canEdit && (att.uploaded_by === currentUserId || canEdit);
              // Direct backend URL — auth flows via the `access_token` + `current_org_id`
              // cookies set on login, so the browser opens the file natively without
              // any JS popup-blocker dance. Backend serves Content-Disposition: inline
              // for images/PDFs so they render in the new tab; xlsx/docx/zip etc still
              // download because the browser can't render them inline.
              const previewUrl = `${API}/attachments/${att.attachment_id}/download`;
              const downloadUrl = `${API}/attachments/${att.attachment_id}/download?download=1`;
              return (
                <div key={att.attachment_id} className="bg-white rounded-md border border-zinc-200 hover:border-zinc-300 hover:shadow-sm transition-all p-2 group" data-testid={`attachment-${att.attachment_id}`}>
                  {isImage ? (
                    <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="w-full block mb-1.5">
                      <ImageThumb att={att} />
                    </a>
                  ) : (
                    <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="block">
                      <div className="flex items-center justify-center bg-zinc-50 rounded-md py-4 mb-1.5 hover:bg-zinc-100 transition-colors">
                        <Icon size={32} className="text-zinc-400" weight="light" />
                      </div>
                    </a>
                  )}
                  <div className="flex items-start justify-between gap-2 min-w-0">
                    <div className="min-w-0 flex-1">
                      <a
                        href={previewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-[12px] font-medium text-zinc-800 hover:text-blue-600 truncate text-left w-full"
                        title={inlineable ? `Preview ${att.original_filename}` : att.original_filename}
                        data-testid={`attachment-name-${att.attachment_id}`}
                      >
                        {att.original_filename}
                      </a>
                      <p className="text-[10px] text-zinc-500 truncate">
                        {fmtBytes(att.size_bytes)} · {att.uploader?.name || 'Unknown'} · {timeAgo(att.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      <a
                        href={downloadUrl}
                        download={att.original_filename || 'file'}
                        className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-zinc-100 text-zinc-500 hover:text-blue-600"
                        title="Download"
                        data-testid={`attachment-download-${att.attachment_id}`}
                      >
                        <DownloadSimple size={14} />
                      </a>
                      {canDelete && (
                        <button
                          onClick={() => handleDelete(att)}
                          className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-red-50 text-zinc-400 hover:text-red-600"
                          title="Delete"
                          data-testid={`attachment-delete-${att.attachment_id}`}
                        >
                          <Trash size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
